// AdminUsersTab.jsx — Users management (table + create + pwd + edit + delete)
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./SettingsUsers.css";

export default function AdminUsersTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return list;
    const s = q.toLowerCase();
    return list.filter(
      (u) =>
        (u.username || "").toLowerCase().includes(s) ||
        (u.email || "").toLowerCase().includes(s) ||
        (u.display_name || "").toLowerCase().includes(s)
    );
  }, [q, list]);

  const [selected, setSelected] = useState(null);

  const [createForm, setCreateForm] = useState({
    username: "",
    password: "",
    display_name: "",
    email: "",
    job_title: "",
    phone_number: "",
    location: "",
    user_type: "viewer",
  });

  const [pwdUser, setPwdUser] = useState("");
  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");

  // Load users
  const fetchUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get("/api/admin/users.php", { withCredentials: true });
      const rows = res.data?.data ?? res.data ?? [];
      setList(Array.isArray(rows) ? rows : []);
    } catch {
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const selectRow = (u) => {
    setSelected(u);
    setPwdUser(u?.username || "");
  };

  // Create user
  const onCreate = async () => {
    setError("");
    const f = createForm;
    if (!f.username || !f.password || !f.display_name || !f.email) {
      setError("Missing required fields");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post("/api/admin/users.php", f, { withCredentials: true });
      if (!res.data?.success) throw new Error(res.data?.error || "Create failed");
      setCreateForm({
        username: "",
        password: "",
        display_name: "",
        email: "",
        job_title: "",
        phone_number: "",
        location: "",
        user_type: "viewer",
      });
      await fetchUsers();
    } catch (e) {
      setError(e.message || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  // Change password
  const onChangePwd = async () => {
    setError("");
    if (!pwdUser || !pwd1 || !pwd2) return setError("Password form incomplete");
    if (pwd1 !== pwd2) return setError("Passwords do not match");
    setBusy(true);
    try {
      const res = await axios.post(
        "/api/admin/user_password.php",
        {
        id: selected?.id || 0,        // 👈 ajoute l’id du user sélectionné
        username: pwdUser,
        new_password: pwd1,
        },
        { withCredentials: true }
      );
      if (!res.data?.success) throw new Error(res.data?.error || "Password update failed");
      setPwd1("");
      setPwd2("");
    } catch (e) {
      setError(e.message || "Password update failed");
    } finally {
      setBusy(false);
    }
  };

  // Update user info
  const onSaveInfo = async () => {
    setError("");
    if (!selected?.username) return setError("Select a user first");
    setBusy(true);
    try {
      const payload = {
        id: selected.id,
        username: selected.username,
        display_name: selected.display_name,
        email: selected.email,
        job_title: selected.job_title || null,
        phone_number: selected.phone_number || null,
        location: selected.location || "",
        status: selected.status || "active",
        user_type: selected.user_type || "viewer",
      };
      const res = await axios.put("/api/admin/users.php", payload, { withCredentials: true });
      if (!res.data?.success) throw new Error(res.data?.error || "Update failed");
      await fetchUsers();
      const updated = (res.data?.user) || list.find((u) => u.username === payload.username);
      setSelected(updated || null);
    } catch (e) {
      setError(e.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  // Delete selected user
  const onDelete = async () => {
    if (!selected?.id) return;
    if (!window.confirm(`Delete user "${selected.username}"? This cannot be undone.`)) return;
    setBusy(true);
    setError("");
    try {
      const res = await axios.delete("/api/admin/users.php", {
        withCredentials: true,
        data: { id: selected.id },
      });
      if (!res.data?.success) throw new Error(res.data?.error || "Delete failed");
      setSelected(null);
      await fetchUsers();
    } catch (e) {
      setError(e.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Search + Table */}
      <div className="users-card">
        <input
          className="form-control bg-dark text-light border-0 mb-3"
          placeholder="Search username, email, display name"
          autoComplete="off"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="users-table-wrap">
          <table className="table table-dark table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Username</th>
                <th>Display</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Location</th>
                <th>Last login</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="7" className="text-center text-muted">No users</td></tr>
              ) : (
                filtered.map((u) => (
                  <tr
                    key={u.id}
                    className={`row-selectable ${selected?.username === u.username ? "table-active" : ""}`}
                    onClick={() => selectRow(u)}
                  >
                    <td>{u.username}</td>
                    <td>{u.display_name}</td>
                    <td>{u.email}</td>
                    <td><span className={`role-badge ${u.user_type}`}>{u.user_type}</span></td>
                    <td>{u.status}</td>
                    <td>{u.location || "—"}</td>
                    <td>{u.last_logged_in ? new Date(u.last_logged_in).toLocaleString() : "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create user */}
      <div className="users-card">
        <div className="section-title">Create user</div>
        <div className="users-info">
          {[
            ["Username *", "username", "text", "new-username"],
            ["Password *", "password", "password", "new-password"],
            ["Display name *", "display_name", "text", "off"],
            ["Email *", "email", "email", "new-email"],
            ["Job title", "job_title", "text", "off"],
            ["Phone", "phone_number", "text", "off"],
            ["Location", "location", "text", "off"],
          ].map(([label, field, type, auto]) => (
            <div key={field} className="info-row">
              <span>{label}</span>
              <input
                type={type}
                className="form-control form-control-sm"
                autoComplete={auto}
                value={createForm[field]}
                onChange={(e) => setCreateForm((p) => ({ ...p, [field]: e.target.value }))}
              />
            </div>
          ))}
          <div className="info-row">
            <span>Role</span>
            <select
              className="form-select form-select-sm"
              value={createForm.user_type}
              onChange={(e) => setCreateForm((p) => ({ ...p, user_type: e.target.value }))}
            >
              <option value="viewer">viewer</option>
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </div>
        </div>
        <div className="text-end mt-3">
          <button className="btn-profile btn-success" onClick={onCreate} disabled={busy}>
            {busy ? "Saving..." : "Create"}
          </button>
        </div>
      </div>

      {/* Change password */}
      <div className="users-card">
        <div className="section-title">Change password</div>
        <div className="users-info">
          <div className="info-row">
            <span>Username</span>
            <input
              className="form-control form-control-sm"
              autoComplete="off"
              value={pwdUser}
              onChange={(e) => setPwdUser(e.target.value)}
            />
          </div>
          <div className="info-row">
            <span>New password</span>
            <input
              type="password"
              className="form-control form-control-sm"
              autoComplete="new-password"
              value={pwd1}
              onChange={(e) => setPwd1(e.target.value)}
            />
          </div>
          <div className="info-row">
            <span>Confirm password</span>
            <input
              type="password"
              className="form-control form-control-sm"
              autoComplete="new-password"
              value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
            />
          </div>
        </div>
        <div className="text-end mt-3">
          <button className="btn-profile btn-success" onClick={onChangePwd} disabled={busy}>
            {busy ? "Saving..." : "Update"}
          </button>
        </div>
      </div>

      {/* Edit + Delete */}
      <div className="users-card">
        <div className="section-title">Edit account info</div>
        <div className="users-info">
          {[
            ["Username", "username"],
            ["Role", "user_type", "select"],
            ["Display name", "display_name"],
            ["Email", "email"],
            ["Job title", "job_title"],
            ["Phone", "phone_number"],
            ["Location", "location"],
            ["Status", "status", "select"],
          ].map(([label, field, type]) => (
            <div key={field} className="info-row">
              <span>{label}</span>
              {type === "select" ? (
                <select
                  className="form-select form-select-sm"
                  value={selected?.[field] || (field === "status" ? "active" : "viewer")}
                  onChange={(e) => setSelected((s) => ({ ...(s || {}), [field]: e.target.value }))}
                >
                  {field === "user_type"
                    ? ["viewer", "user", "admin"].map((r) => <option key={r}>{r}</option>)
                    : ["active", "inactive"].map((r) => <option key={r}>{r}</option>)}
                </select>
              ) : (
                <input
                  className="form-control form-control-sm"
                  autoComplete="off"
                  value={selected?.[field] || ""}
                  onChange={(e) => setSelected((s) => ({ ...(s || {}), [field]: e.target.value }))}
                />
              )}
            </div>
          ))}
        </div>
        <div className="d-flex gap-2 justify-content-end mt-3">
          <button className="btn-profile btn-danger" onClick={onDelete} disabled={busy || !selected}>
            {busy ? "Working..." : "Delete"}
          </button>
          <button className="btn-profile btn-success" onClick={onSaveInfo} disabled={busy || !selected}>
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger text-center py-2">{error}</div>}
    </>
  );
}
